import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { LicensePicker } from "@/components/studio/LicensePicker";
import { GarmentLibrary } from "@/components/studio/GarmentLibrary";
import { SceneComposer } from "@/components/studio/SceneComposer";
import { GenerationGallery } from "@/components/studio/GenerationGallery";
import { ArrowLeft, FileKey } from "lucide-react";

export default function Studio() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [licenses, setLicenses] = useState([]);
  const [presets, setPresets] = useState([]);
  const [fee, setFee] = useState(5);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [license, setLicense] = useState(null);
  const [generations, setGenerations] = useState([]);
  const [garments, setGarments] = useState([]);
  const [selectedGarment, setSelectedGarment] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [opening, setOpening] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  // garment upload form
  const [gName, setGName] = useState("");
  const [gType, setGType] = useState("flat_lay");
  const [gUploading, setGUploading] = useState(false);
  const gFileRef = useRef(null);

  const loadBase = useCallback(async () => {
    try {
      const [licRes, presetRes, walletRes, garmentsRes] = await Promise.all([
        api.get("/studio/licenses"),
        api.get("/studio/presets"),
        api.get("/wallet"),
        api.get("/garments"),
      ]);
      setLicenses(licRes.data);
      setPresets(presetRes.data.presets);
      setFee(presetRes.data.generation_fee);
      setBalance(walletRes.data.balance);
      setGarments(garmentsRes.data);
      return licRes.data;
    } catch (e) {
      toast.error(errMsg(e, "Could not load the Studio"));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSession = useCallback(async (sessionId) => {
    try {
      const { data } = await api.get(`/studio/sessions/${sessionId}`);
      setSession(data.session);
      setGenerations(data.generations);
      setLicense(data.license);
    } catch (e) {
      toast.error(errMsg(e, "Could not load the session"));
    }
  }, []);

  const openSession = useCallback(async (lic, silent = false) => {
    setOpening(lic.id);
    try {
      const { data } = await api.post("/studio/sessions", { license_id: lic.id });
      await loadSession(data.id);
      if (!silent) toast.success(`Secure twin session opened for ${lic.model_name}`);
    } catch (e) {
      toast.error(errMsg(e, "Could not open a studio session"));
    } finally {
      setOpening(null);
    }
  }, [loadSession]);

  const bootstrappedRef = useRef(false);
  useEffect(() => {
    if (!user || bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    loadBase().then((lics) => {
      const target = searchParams.get("license");
      if (target) {
        const lic = lics.find((l) => l.id === target);
        if (lic) openSession(lic, true);
      }
    });
  }, [user, loadBase, searchParams, openSession]);

  const leaveSession = () => {
    setSession(null);
    setLicense(null);
    setGenerations([]);
    setSelectedGarment(null);
    setSelectedPreset(null);
    setCustomPrompt("");
    setSearchParams({});
    loadBase();
  };

  const uploadGarment = async (files) => {
    if (!files?.length) return;
    if (!gName.trim()) {
      toast.error("Name the garment first (e.g. 'Blue Summer Dress')");
      if (gFileRef.current) gFileRef.current.value = "";
      return;
    }
    setGUploading(true);
    try {
      const fd = new FormData();
      fd.append("name", gName.trim());
      fd.append("garment_type", gType);
      fd.append("file", files[0]);
      const { data } = await api.post("/garments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGarments((g) => [data, ...g]);
      setSelectedGarment(data);
      setGName("");
      toast.success(`'${data.name}' added to your garment library`);
    } catch (e) {
      toast.error(errMsg(e, "Garment upload failed"));
    } finally {
      setGUploading(false);
      if (gFileRef.current) gFileRef.current.value = "";
    }
  };

  const deleteGarment = async (g) => {
    try {
      await api.delete(`/garments/${g.id}`);
      setGarments((list) => list.filter((x) => x.id !== g.id));
      if (selectedGarment?.id === g.id) setSelectedGarment(null);
    } catch (e) {
      toast.error(errMsg(e, "Could not delete garment"));
    }
  };

  const generate = async () => {
    if (!selectedGarment) return toast.error("Select a garment from your library first");
    if (!selectedPreset && !customPrompt.trim()) return toast.error("Pick a preset background or describe a scene");
    setGenerating(true);
    try {
      const { data } = await api.post("/studio/generate", {
        session_id: session.id,
        garment_id: selectedGarment.id,
        preset_key: selectedPreset,
        prompt: customPrompt.trim(),
      }, { timeout: 240000 });
      setGenerations((g) => [data, ...g]);
      setBalance((b) => (b !== null ? Math.round((b - fee) * 100) / 100 : b));
      toast.success(`Image generated — ${session.model_name} wearing '${selectedGarment.name}'`);
    } catch (e) {
      toast.error(errMsg(e, "Generation failed — you were not charged"));
    } finally {
      setGenerating(false);
    }
  };

  const exportGeneration = async (gen) => {
    setExportingId(gen.id);
    try {
      const { data } = await api.post(`/studio/generations/${gen.id}/export`);
      setGenerations((list) => list.map((g) => (g.id === gen.id ? data.generation : g)));
      setSession((s) => ({ ...s, status: "closed" }));
      toast.success("Final image exported — the temporary twin session is now closed", {
        description: "You received the flat watermarked file only. The model's AI profile stays locked to LOOKBOOK.",
      });
      window.open(`${api.defaults.baseURL}/images/${data.download_path}`, "_blank");
    } catch (e) {
      toast.error(errMsg(e, "Export failed"));
    } finally {
      setExportingId(null);
    }
  };

  if (!authLoading && (!user || user.role !== "client")) {
    return (
      <div className="page-container py-20 text-center">
        <p className="font-display text-2xl">The Creation Studio is for brand clients</p>
        <p className="mt-2 text-sm text-muted-foreground">Models and agencies can follow their twins' activity from their dashboard.</p>
        <Button className="mt-4 rounded-xl" onClick={() => navigate(user ? "/dashboard" : "/auth")}>
          {user ? "Go to dashboard" : "Sign in"}
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container space-y-6 py-10">
        <Skeleton className="h-10 w-80" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-44 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  /* ---------- LICENSE / TWIN PICKER ---------- */
  if (!session) {
    return <LicensePicker licenses={licenses} fee={fee} opening={opening} onOpenSession={openSession} />;
  }

  /* ---------- SESSION WORKSPACE ---------- */
  const sessionClosed = session.status === "closed";
  return (
    <div className="page-container py-8 sm:py-10">
      <button data-testid="studio-back-button" onClick={leaveSession}
        className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground transition-[color] hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> All twins
      </button>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Creation Studio</p>
          <h1 className="font-display text-3xl tracking-[-0.01em]">{session.model_name}'s digital twin</h1>
          <p className="mt-1 flex items-center gap-2 font-mono-plex text-xs text-muted-foreground">
            <FileKey className="h-3.5 w-3.5" /> Licence {session.license_id.slice(0, 8).toUpperCase()}
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              sessionClosed ? "bg-muted text-muted-foreground" : "bg-[hsl(174,38%,92%)] text-[hsl(174,45%,20%)]"
            }`}>
              {sessionClosed ? "Session closed" : "Secure session open"}
            </span>
          </p>
        </div>
        <Badge data-testid="studio-wallet-badge" variant="secondary" className="rounded-full px-3 py-1 font-mono-plex text-xs">
          Wallet {balance !== null ? fmtMoney(balance) : "…"} · {fmtMoney(fee)}/image
        </Badge>
      </div>

      {sessionClosed && (
        <div data-testid="studio-session-closed-banner" className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-[hsl(174,38%,70%)] bg-[hsl(174,38%,96%)] p-4 text-sm">
          <p className="text-[hsl(174,45%,18%)]">
            <span className="font-semibold">Final image exported — this temporary twin session is closed.</span>{" "}
            Open a new session to keep creating under this licence.
          </p>
          <Button data-testid="studio-new-session-button" size="sm" className="rounded-xl"
            onClick={() => openSession({ id: session.license_id, model_name: session.model_name })}>
            Start new session
          </Button>
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[320px_1fr]">
        <GarmentLibrary
          garments={garments}
          selectedGarment={selectedGarment}
          onSelectGarment={setSelectedGarment}
          onDeleteGarment={deleteGarment}
          gName={gName}
          setGName={setGName}
          gType={gType}
          setGType={setGType}
          gUploading={gUploading}
          gFileRef={gFileRef}
          onUploadGarment={uploadGarment}
        />

        <div className="space-y-6">
          <SceneComposer
            presets={presets}
            selectedPreset={selectedPreset}
            setSelectedPreset={setSelectedPreset}
            customPrompt={customPrompt}
            setCustomPrompt={setCustomPrompt}
            selectedGarment={selectedGarment}
            generating={generating}
            sessionClosed={sessionClosed}
            fee={fee}
            onGenerate={generate}
          />
          <GenerationGallery
            generations={generations}
            generating={generating}
            selectedGarment={selectedGarment}
            session={session}
            exportingId={exportingId}
            onExport={exportGeneration}
          />
        </div>
      </div>
    </div>
  );
}
